import {
  CARACTERE_DO_SEPARADOR,
  TAMANHO_MAXIMO_DO_CSV,
  type Decisao,
  type EtapaDaImportacao,
  type ImportacaoDetalhada,
  type ResultadoDaLinha,
  type SituacaoDaLinha,
} from '@arena/shared/schemas';
import type { Situacao } from '../ui/primitivos.js';

/**
 * Os textos da importação. Funções puras — nenhuma toca a rede nem o DOM.
 */

const plural = (n: number, um: string, varios: string) =>
  `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

/**
 * O toast do protótipo, que o documento 09 fixa como comportamento:
 * "Pré-visualização: 214 linhas válidas, 6 com telefone duplicado."
 *
 * "Com telefone duplicado" só quando é verdade: o CPF repetido também
 * marca a linha como duplicada, e aí o texto diz os dois.
 */
export function resumoDaAnalise(
  importacao: Pick<ImportacaoDetalhada, 'contadores' | 'porDiagnostico'>,
): string {
  const { validas, duplicadas, bloqueadas } = importacao.contadores;
  const porCpf =
    importacao.porDiagnostico.cpf_existente + importacao.porDiagnostico.cpf_duplicado_no_arquivo;

  const partes = [plural(validas, 'linha válida', 'linhas válidas')];
  if (duplicadas > 0) {
    partes.push(
      porCpf === 0
        ? `${duplicadas.toLocaleString('pt-BR')} com telefone duplicado`
        : `${duplicadas.toLocaleString('pt-BR')} com telefone ou CPF duplicado`,
    );
  }
  if (bloqueadas > 0) partes.push(`${bloqueadas.toLocaleString('pt-BR')} para corrigir`);
  return `Pré-visualização: ${partes.join(', ')}.`;
}

/**
 * O que dá para dizer do arquivo antes de enviá-lo. O servidor confere
 * tudo de novo; aqui é para a pessoa não esperar o envio de um arquivo
 * que vai ser recusado — e para receber a mensagem certa, que um upload
 * interrompido no meio não entregaria.
 */
export function problemaDoArquivo(arquivo: {
  readonly name: string;
  readonly size: number;
}): string | null {
  if (/\.(xlsx|xlsm|xls|ods|numbers)$/i.test(arquivo.name)) {
    return (
      'Este arquivo é uma planilha, não um CSV. No Excel, use "Salvar como" e escolha ' +
      '"CSV (separado por vírgulas)" — depois importe o arquivo .csv.'
    );
  }
  if (arquivo.size === 0) return 'O arquivo está vazio.';
  if (arquivo.size > TAMANHO_MAXIMO_DO_CSV) {
    return (
      `O arquivo tem ${megabytes(arquivo.size, 'cima')} MB; o limite é ` +
      `${megabytes(TAMANHO_MAXIMO_DO_CSV, 'baixo')} MB por importação. Divida a planilha em partes.`
    );
  }
  return null;
}

/** "1,4 MB", "70 KB" — o tamanho no subtítulo da conferência. */
export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024 * 1024)
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('pt-BR')} KB`;
  return `${megabytes(bytes, 'cima')} MB`;
}

function megabytes(bytes: number, arredondar: 'cima' | 'baixo'): string {
  const decimos = (bytes / (1024 * 1024)) * 10;
  const valor = (arredondar === 'cima' ? Math.ceil(decimos) : Math.floor(decimos)) / 10;
  return valor.toLocaleString('pt-BR');
}

/** ";" → "Ponto e vírgula (;)" — o que foi detectado, com o caractere à vista. */
export function separadorLegivel(
  rotulo: string,
  separador: keyof typeof CARACTERE_DO_SEPARADOR,
): string {
  const caractere = CARACTERE_DO_SEPARADOR[separador];
  return caractere === '\t' ? rotulo : `${rotulo} (${caractere})`;
}

/** Quanto da gravação já andou, de 0 a 100. */
export function percentualGravado(
  importacao: Pick<ImportacaoDetalhada, 'contadores' | 'progresso'>,
): number {
  const { total } = importacao.contadores;
  if (total === 0) return 100;
  const { criados, atualizados, pulados, falhas } = importacao.progresso;
  return Math.min(100, Math.round(((criados + atualizados + pulados + falhas) / total) * 100));
}

export const SITUACAO_DA_LINHA: Readonly<Record<SituacaoDaLinha, Situacao>> = {
  valida: 'ok',
  duplicada: 'atencao',
  bloqueada: 'atrasada',
};

export const ROTULO_SITUACAO_DA_LINHA: Readonly<Record<SituacaoDaLinha, string>> = {
  valida: 'Válida',
  duplicada: 'Duplicada',
  bloqueada: 'Bloqueada',
};

export const SITUACAO_DA_ETAPA: Readonly<Record<EtapaDaImportacao, Situacao>> = {
  analisada: 'aguardando',
  gravando: 'atencao',
  concluida: 'ok',
  descartada: 'neutra',
};

export const SITUACAO_DO_RESULTADO: Readonly<Record<ResultadoDaLinha, Situacao>> = {
  criado: 'ok',
  atualizado: 'ok',
  pulado: 'neutra',
  falha: 'atrasada',
};

export const ROTULO_RESULTADO: Readonly<Record<ResultadoDaLinha, string>> = {
  criado: 'Criado',
  atualizado: 'Atualizado',
  pulado: 'Pulado',
  falha: 'Não entrou',
};

/**
 * A decisão da linha num `<select>` só. "Atualizar" carrega o cadastro
 * alvo no valor — `atualizar:<id>` —, porque a linha pode ter mais de um
 * candidato, e escolher a ação e o alvo em dois campos deixaria um estado
 * no meio ("atualizar quem?") que o servidor recusaria.
 */
export function valorDaDecisao(decisao: Decisao, alunoAlvoId: string | null): string {
  return decisao === 'atualizar' && alunoAlvoId ? `atualizar:${alunoAlvoId}` : decisao;
}

export function decisaoDoValor(valor: string): { decisao: Decisao; alunoAlvoId?: string } {
  if (valor.startsWith('atualizar:')) return { decisao: 'atualizar', alunoAlvoId: valor.slice(10) };
  return { decisao: valor as Decisao };
}
